import { useRef, useLayoutEffect, useCallback } from 'react';

/**
 * Scales font size down to fit text within its container without truncation.
 * Re-measures on container resize via ResizeObserver.
 */
export function useAutoFitText<T extends HTMLElement = HTMLElement>(text: string, minScale = 0.55) {
  const ref = useRef<T>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    const container = el?.parentElement;
    if (!el || !container) return;

    el.style.fontSize = '';
    const available = container.clientWidth;
    const natural = el.scrollWidth;

    if (natural > available && available > 0) {
      const scale = Math.max(minScale, available / natural);
      el.style.fontSize = `${scale}em`;
    }
  }, [minScale]);

  useLayoutEffect(() => {
    fit();
  }, [text, fit]);

  useLayoutEffect(() => {
    const container = ref.current?.parentElement;
    if (!container) return;

    const ro = new ResizeObserver(fit);
    ro.observe(container);
    return () => ro.disconnect();
  }, [fit]);

  return ref;
}
